import I18n from 'I18n';
import unescapedText from 'I18n/utils/unescapedText';
import { SECTIONS } from 'chatspot-core/messages/DisplayTypes';
import * as Tools from 'copilot-toolkit/constants/Tools';
import enviro from 'enviro';
import { mapChirpCitationToCitationCore } from '../../utils/citationsUtil';
import { getDashworksSearchToolStartMessage } from './dashworksToolMessages';
import Raven from 'raven-js';

// eslint-disable-next-line @typescript-eslint/no-explicit-any

export const createInitialCopilotV2Message = (sections, timestamp, userMessage) => ({
  id: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.id) || 0,
  portalId: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.portalId) || 0,
  userId: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.userId) || 0,
  threadId: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.threadId) || 0,
  correlationId: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.correlationId) || '',
  createdAt: timestamp,
  toolCalls: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.toolCalls) || [],
  state: 'streaming',
  messageType: 'COPILOT',
  sections,
  parentMessageId: (userMessage === null || userMessage === void 0 ? void 0 : userMessage.parentMessageId) || 0
});
export const mapCopilotV2MessageTypeToMessageType = messageType => {
  switch (messageType) {
    case 'USER':
      return 'user';
    case 'COPILOT':
      return 'chatspot';
    default:
      throw new Error(`Unknown message type: ${String(messageType)}`);
  }
};
const mapCopilotV2ActionTypenameToActionType = typename => {
  switch (typename) {
    case 'com.hubspot.chatspot.message.schemas.buttons.ActionButton':
      return 'ACTION';
    case 'com.hubspot.chatspot.message.schemas.buttons.ActionButtonV2':
      return 'ACTION_V2';
    case 'com.hubspot.chatspot.message.schemas.buttons.LinkButton':
      return 'LINK';
    case 'com.hubspot.chatspot.message.schemas.buttons.TemplateButton':
      return 'TEMPLATE';
    default:
      throw new Error(`Unknown action type: ${typename}`);
  }
};

/**
 * CHIRP RPC wraps the data table rows in a  `value` object. This function
 * unwraps the value object and maps the data table rows to the correct type
 * so we can use the existing DataTable renderer.
 */
const mapCopilotV2DataTableSectionToDataTableSection = section => {
  return Object.assign({}, section, {
    type: 'DATA_TABLE',
    dataTable: Object.assign({}, section.dataTable, {
      data: section.dataTable.data.map(row => {
        return row.value || row;
      })
    })
  });
};
export const mapCopilotV2SectionsToSections = sections => {
  // TODO - @mlegocki: thread-endpoints are returning a diffeerent message schema compared to streaming messages
  // resolve this type issue with BE. We should be getting consistent types for messages in threads vs. streaming messages
  return sections.map(section => {
    if (section.__typename && !section.type) {
      /**
       * Our section renderers expect a specific section type. Currently, there is a issue
       * where the CHIRP endpoint removes the section type so we will map the typename
       * to the corresponding section type.
       */

      switch (section.__typename) {
        case 'com.hubspot.chatspot.message.schemas.sections.UserChatResponseSection':
        case 'com.hubspot.chatspot.message.schemas.sections.UserActionResponseSection':
          return Object.assign({}, section, {
            type: 'PLAIN_TEXT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ErrorResponseSection':
          return Object.assign({}, section, {
            type: 'ERROR'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.HtmlTextResponseSection':
          return Object.assign({}, section, {
            type: 'HTML'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.PlainTextResponseSection':
          return Object.assign({}, section, {
            type: 'PLAIN_TEXT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.DataTableResponseSection':
          return mapCopilotV2DataTableSectionToDataTableSection(section);
        case 'com.hubspot.chatspot.message.schemas.sections.ThoughtResponseSection':
          return Object.assign({}, section, {
            type: 'THOUGHT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ActionsResponseSection':
          return Object.assign({}, section, {
            actions: section.actions.map(action => Object.assign({}, action, {
              type: mapCopilotV2ActionTypenameToActionType(action.__typename)
            })),
            type: 'ACTION'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.AccordionResponseSection':
          return Object.assign({}, section, {
            type: 'ACCORDION'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.card.CardResponseSection':
          return Object.assign({}, section, {
            type: 'CARD'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ConfirmationResponseSection':
          return Object.assign({}, section, {
            type: 'CONFIRMATION'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.image.ImageGridResponseSection':
          return Object.assign({}, section, {
            type: 'IMAGE_GRID'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.media.MediaPreviewResponseSection':
          return Object.assign({}, section, {
            type: 'MEDIA'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.media.MediaPreviewResponseSectionV2':
          return Object.assign({}, section, {
            type: 'MEDIA_V2'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.meeting.MeetingResponseSection':
          return Object.assign({}, section, {
            type: 'MEETING'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.LlmResponseSection':
          return Object.assign({}, section, {
            type: 'LLM'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.ReportResponseSection':
          return Object.assign({}, section, {
            type: 'REPORT'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.FilterBranchResponseSection':
          return Object.assign({}, section, {
            type: 'FILTER_BRANCH'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.DenseListResponseSection':
          return Object.assign({}, section, {
            type: 'DENSE_LIST'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.CollaborationResponseSection':
          return Object.assign({}, section, {
            type: 'COLLABORATE'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.CrmCardResponseSection':
          return Object.assign({}, section, {
            type: 'CRM_CARD'
          });
        case 'com.hubspot.chatspot.message.schemas.sections.file.FileAttachmentResponseSection':
          return Object.assign({}, section, {
            type: 'FILE_ATTACHMENT'
          });
        default:
          // TODO: We don't have renderers for:
          // com.hubspot.chatspot.message.schemas.sections.BarChartResponseSection
          // com.hubspot.chatspot.message.schemas.sections.DashboardResponseSection
          Raven.captureMessage(`Unknown section type: ${section.__typename}`, {
            level: 'error'
          });
          return section;
      }
    }
    switch (section.type) {
      case 'USER_CHAT':
        // We don't have a renderer for USER_CHAT so we will map it to PLAIN_TEXT.
        // This is a temporary solution until we have a better way to render it in a V2 Chat component.
        return Object.assign({}, section, {
          type: 'PLAIN_TEXT'
        });
      case 'DATA_TABLE':
        return mapCopilotV2DataTableSectionToDataTableSection(section);
      default:
        return section;
    }
  });
};
export const copilotV2MessageToMessageWithSections = message => {
  const messageWithSections = {
    actions: [],
    id: message.id,
    query: {
      intent: ''
    },
    response: '',
    source: 'CHAT',
    thread_id: message.threadId,
    time: message.createdAt || Date.now(),
    type: mapCopilotV2MessageTypeToMessageType(message.messageType),
    value: '',
    displayType: SECTIONS,
    sections: mapCopilotV2SectionsToSections(message.sections)
  };
  return messageWithSections;
};
export const copilotV2ResponseMessageSectionToCopilotV2Message = (section, state) => {
  return {
    id: Math.random(),
    portalId: 0,
    userId: 0,
    threadId: 0,
    correlationId: '',
    createdAt: '',
    toolCalls: [],
    state,
    sections: [section]
  };
};
export const createCopilotDurationMessageSection = duration => {
  return {
    id: '',
    // TODO - @jowilson i18n and create a custom renderer for the duration.
    text: `<small>${I18n.text(`reasoningMessage.duration`, {
      duration: Math.round(duration / 1000)
    })}</small>`,
    type: 'LLM'
  };
};
export const createCopilotExceptionMessage = sections => {
  const errorSection = {
    // only 1 section is sent in the exception message
    id: sections[0].id,
    type: 'LLM',
    text: sections[0].text
  };
  return createInitialCopilotV2Message([errorSection], Date.now());
};

/**
 * @temporary This utility is used for creating action exception message section when we call a V1 action that is not supported in V2.
 * @expiration 2025-10-15
 * @deprecated
 * This happens when a valid V1 action cannot be translated to a V2 tool call
 */
export const createCopilotMessageActionExceptionSection = () => {
  return {
    id: '',
    text: I18n.text('messageActions.exception.message'),
    type: 'LLM'
  };
};
export const getCopilotV2ToolStartText = (toolStart, connectedApps, appSearchScope) => {
  switch (toolStart.toolName) {
    case Tools.ASK_HUBSPOT:
      return unescapedText('toolCallStatus.ask_hubspot');
    case Tools.CRM_OBJECT_SEARCH:
    case Tools.CRM_OBJECT_SEARCH_V2:
    case Tools.CRM_BASIC_SEARCH:
    case Tools.CRM_OBJECT_LIST:
      return unescapedText('toolCallStatus.crm_object_search');
    case Tools.CRM_OBJECT_SUMMARY:
      return unescapedText('toolCallStatus.crm_object_summary');
    case Tools.CRM_OBJECT_UPSERT:
      return unescapedText('toolCallStatus.crm_object_upsert');
    case Tools.DASHWORKS_SEARCH:
      return getDashworksSearchToolStartMessage(connectedApps, appSearchScope);
    case Tools.CRM_META_SEARCH:
      return unescapedText('toolCallStatus.crm_meta_search');
    case Tools.REPORT_BUILDER:
      return unescapedText('toolCallStatus.report_builder');
    case Tools.WEB_SEARCH:
      return unescapedText('toolCallStatus.web_search');
    case Tools.THINK:
      return unescapedText('toolCallStatus.thinking');
    case Tools.OBJECT_SUMMARY:
      return unescapedText('toolCallStatus.object_summary');
    case Tools.PRICING_PACKAGING:
      return unescapedText('toolCallStatus.pricing_packaging');
    case Tools.BUILD_WORKFLOW_ACTIONS:
      return unescapedText('toolCallStatus.build_workflow_actions');
    case Tools.BUILD_WORKFLOW_TRIGGERS:
      return unescapedText('toolCallStatus.build_workflow_triggers');
    case Tools.CREATE_WORKFLOW:
      return unescapedText('toolCallStatus.create_workflow');
    case Tools.VALIDATE_WORKFLOW:
      return unescapedText('toolCallStatus.validate_workflow');
    case Tools.FETCH_AVAILABLE_WORKFLOW_TRIGGERS:
      return unescapedText('toolCallStatus.fetch_workflow_triggers');
    case Tools.FETCH_AVAILABLE_WORKFLOW_ACTIONS:
      return unescapedText('toolCallStatus.fetch_workflow_actions');
    case Tools.FETCH_WORKFLOW_RESOURCES:
      return unescapedText('toolCallStatus.fetch_workflow_resources');
    case Tools.DATA_MODEL_GET_BEST_PRACTICES_AND_RULES:
      return unescapedText('toolCallStatus.DataModelTools_getDataModelBestPracticesAndRules');
    case Tools.DATA_MODEL_GET_PORTAL_CONTEXT:
      return unescapedText('toolCallStatus.DataModelTools_getPortalContext');
    case Tools.DATA_MODEL_FORMAT_DATA_MODEL_CONTENT:
      return unescapedText('toolCallStatus.DataModelTools_formatDataModelContent');
    case Tools.DATA_MODEL_CREATE_CUSTOM_PROPERTIES:
      return unescapedText('toolCallStatus.DataModelTools_createCustomProperties');
    case Tools.DATA_MODEL_CREATE_CUSTOM_PIPELINES:
      return unescapedText('toolCallStatus.DataModelTools_createCustomPipelines');
    case Tools.DATA_MODEL_CREATE_ASSOCIATION_LABELS:
      return unescapedText('toolCallStatus.DataModelTools_createAssociationLabels');
    case Tools.DATA_MODEL_CREATE_CUSTOM_OBJECT_TYPES:
      return unescapedText('toolCallStatus.DataModelTools_createCustomObjectTypes');
    case Tools.CHANGE_FOCUS:
      if (!enviro.isProd()) {
        return unescapedText('toolCallStatus.change_focus');
      }
    // Fall through to default for production
    default:
      {
        // cannot declare for build-time (i.e. in a const outside of this function) due to unescapedText throwing errors
        const defaultStatusMessages = [unescapedText('toolCallStatus.default.working'), unescapedText('toolCallStatus.default.processingYourRequest'), unescapedText('toolCallStatus.default.gettingResults'), unescapedText('toolCallStatus.default.analyzing'), unescapedText('toolCallStatus.default.stillWorking'), unescapedText('toolCallStatus.default.loadingResults'), unescapedText('toolCallStatus.default.checkingEverything'), unescapedText('toolCallStatus.default.almostThere'), unescapedText('toolCallStatus.default.justAMoment')];

        // Randomly select one of the status messages
        const randomIndex = Math.floor(Math.random() * defaultStatusMessages.length);
        return defaultStatusMessages[randomIndex];
      }
  }
};

// API Messages

export const copilotV2ApiMessageToMessageWithSections = message => {
  var _message$citations;
  // copied from __generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService.ts
  // should no longer need once we have proper typing for chatV2
  const feedback = message.feedback ? {
    polarity: message.feedback.polarity,
    tags: message.feedback.tags,
    comment: message.feedback.comment
  } : undefined;
  const messageWithSections = {
    actions: [],
    feedback,
    id: message.id,
    query: {
      intent: ''
    },
    response: '',
    source: 'CHAT',
    // TODO: CHAT vs ACTION vs others?
    thread_id: message.threadId,
    time: message.timestamp || Date.now(),
    type: mapCopilotV2MessageTypeToMessageType(message.messageType),
    value: '',
    displayType: SECTIONS,
    sections: mapCopilotV2SectionsToSections(message.sections),
    citations: (_message$citations = message.citations) === null || _message$citations === void 0 ? void 0 : _message$citations.map(mapChirpCitationToCitationCore)
  };
  return messageWithSections;
};
export const transformCopilotV2ApiMessagesToV1Messages = messages => {
  const groupedMessages = messages.reduce((acc, message) => {
    if (!acc[message.interactionId]) {
      acc[message.interactionId] = [];
    }
    acc[message.interactionId].push(message);
    return acc;
  }, {});
  const transformedMessages = [];
  Object.values(groupedMessages).forEach(group => {
    let copilotMessage = null;
    let userMessage = null;
    group.forEach(msg => {
      if (msg.messageType === 'USER') {
        userMessage = copilotV2ApiMessageToMessageWithSections(msg);
      } else if (msg.messageType === 'COPILOT') {
        if (copilotMessage === null) {
          copilotMessage = copilotV2ApiMessageToMessageWithSections(msg);
        } else {
          copilotMessage.sections.unshift(...mapCopilotV2SectionsToSections(msg.sections));
        }
        copilotMessage.agentPayload = msg.agentPayload;
      }
    });
    if (copilotMessage) {
      transformedMessages.push(copilotMessage);
    }
    if (userMessage) {
      transformedMessages.push(userMessage);
    }
  });
  return transformedMessages;
};